import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { Phone, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import {
  PHONE_LENGTH,
  sanitizePhone,
  validatePhoneNumber,
  validateLoginPassword
} from '../utils/validation';
import './Login.css';

// PHONE_LENGTH handled by utils/validation

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);
  const { login, checkUser } = useAuth();
  const navigate = useNavigate();

  const resetLoginForm = () => {
    setPhoneNumber('');
    setPassword('');
    setError('');
    setPhoneError('');
    setShowPassword(false);
    setCheckComplete(false);
  };

  // Phone number validation - only allow numbers
  const handlePhoneChange = (e) => {
    const value = sanitizePhone(e.target.value);

    // Prevent exceeding PHONE_LENGTH digits
    if (value.length > PHONE_LENGTH) return;

    setPhoneNumber(value);
    setPassword(''); // Reset password when phone changes
    setShowPassword(false);
    setCheckComplete(false); // Reset check state logic

    // Real-time validation
    const error = validatePhoneNumber(value);
    setPhoneError(error);
    setError('');
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPhoneError('');

    const sanitizedPhone = sanitizePhone(phoneNumber);
    const phoneValidationError = validatePhoneNumber(sanitizedPhone);

    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }

    // Check if user exists via Backend API
    const result = await checkUser(sanitizedPhone);

    if (result.success && result.exists) {
      // User exists - show password field for login
      setShowPassword(false); // Reset visibility
      setCheckComplete(true);
    } else if (result.success && !result.exists) {
      // User does not exist - redirect to Register page
      navigate('/register', { state: { phoneNumber: sanitizedPhone } });
    } else {
      // Handle error (e.g. server error)
      setError('Unable to verify phone number. Please try again.');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    const passwordError = validateLoginPassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    const sanitizedPhone = sanitizePhone(phoneNumber);
    const result = await login(sanitizedPhone, password);
    if (result.success) {
      resetLoginForm();
      if (result.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/home');
      }
    } else {
      // If user not found, suggest registration
      if (result.error === 'Invalid phone number or password.') {
        // verification: could be user not found
      }
      setError(result.error);
    }
  };

  // Consolidate validation states into a single memoized object for clean, non-redundant access
  const {
    isButtonDisabled
  } = React.useMemo(() => {
    const sPhone = sanitizePhone(phoneNumber);
    const phoneLengthValid = sPhone.length === PHONE_LENGTH;
    const pError = validatePhoneNumber(sPhone);
    const phoneValid = phoneLengthValid && !pError;

    // Check localStorage freshly when phoneNumber reaches full length
    // With Firebase, we defer this check to the button click or assume existing
    // for the UX flow.
    let exists = false; // We don't check localStorage anymore


    const passError = exists ? validateLoginPassword(password) : '';

    return {
      isButtonDisabled: checkComplete
        ? !password || !!passError // In Login mode
        : !phoneValid // In Continue mode
    };
  }, [phoneNumber, password, checkComplete]);

  const showPasswordField = checkComplete;

  return (
    <div className="login-container">
      <div className="login-icon">
        <img src="/logo.png" alt="Ithal by MR Logo" className="login-logo" />
      </div>

      <div className="login-card fade-in">
        <div className="welcome-section">
          <h1 className="welcome-heading">Welcome!</h1>
          <p className="welcome-subtitle">Enter your phone number to continue</p>
        </div>

        <form onSubmit={showPasswordField ? handleLogin : handlePhoneSubmit} className="login-form">
          <div className="form-group">
            <label>Phone Number</label>
            <div className="input-wrapper">
              <Phone size={20} className="input-icon" />
              <input
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter your phone number"
                value={phoneNumber}
                onChange={handlePhoneChange}
                maxLength={PHONE_LENGTH}
                required
              />
            </div>
            {phoneError && (
              <p className="error-text">{phoneError}</p>
            )}
          </div>

          {showPasswordField && (
            <div className="form-group slide-up">
              <label>Password</label>
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="password-input"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message fade-in">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            className={`btn-primary ${isButtonDisabled ? 'disabled' : ''}`}
            disabled={isButtonDisabled}
          >
            {showPasswordField ? 'Login' : 'Continue'} →
          </button>
          <p
            className="forgot-password-text"
            onClick={() => navigate("/forgot-password")}
          >
            Forgot Password?
          </p>
        </form>
      </div>

      <div className="login-footer-text">
        <p>By continuing, you agree to our Terms &amp; Privacy Policy</p>
      </div>
    </div>
  );
};

export default Login;